import nipplejs from "nipplejs";
import { Tank } from "../Tank";
import { TANK_CONFIG } from "../config";
import * as THREE from "three";
import { CAMERA_CONFIG } from "../config";

export class MobileController {
  private movementJoystick: nipplejs.JoystickManager | null = null;
  private turretJoystick: nipplejs.JoystickManager | null = null;
  private movementZone: HTMLElement | null = null;
  private turretZone: HTMLElement | null = null;
  private isInitialized: boolean = false;
  private isMobile: boolean = false;
  private tank: Tank;
  private originalCameraDistance: number | null = null;
  private mobileCameraDistance: number = 6.5;

  constructor(tank: Tank) {
    this.tank = tank;

    // Check if we're on a mobile device
    this.isMobile = this.detectMobile();

    // Initialize immediately if on mobile
    if (this.isMobile) {
      this.initialize();
    }

    // Listen for orientation changes
    window.addEventListener("orientationchange", () => {
      console.log("[MobileController] Orientation changed");

      // Small delay to ensure DOM is updated after orientation change
      setTimeout(() => {
        // Reinitialize controls if needed
        if (this.isMobile && !this.isInitialized) {
          this.initialize();
        }
      }, 300);
    });

    // Listen for resize events
    window.addEventListener("resize", this.resize.bind(this));
  }

  public initialize(): void {
    if (this.isInitialized) return;

    // Add isMobile class to body to enable mobile control styles
    document.body.classList.add("isMobile");
    console.log("[MobileController] Added isMobile class to body");

    this.setMobileCameraDistance();

    this.createJoystickZones();

    // Initialize movement joystick
    if (this.movementZone) {
      this.movementJoystick = nipplejs.create({
        zone: this.movementZone,
        mode: "static",
        position: { left: "50%", top: "50%" },
        color: "rgba(255, 255, 255, 0.5)",
        size: 120,
        lockX: false,
        lockY: false,
      });

      // Setup movement joystick events
      this.setupMovementJoystickEvents();
    }

    // Initialize turret joystick
    if (this.turretZone) {
      this.turretJoystick = nipplejs.create({
        zone: this.turretZone,
        mode: "static",
        position: { left: "50%", top: "50%" },
        color: "rgba(255, 255, 255, 0.5)",
        size: 120,
        lockX: false,
        lockY: false,
      });

      // Setup turret joystick events
      this.setupTurretJoystickEvents();
    }

    this.isInitialized = true;
  }

  private createJoystickZones(): void {
    // Create movement joystick zone
    this.movementZone = document.createElement("div");
    this.movementZone.id = "movement-joystick";
    this.movementZone.classList.add("joystick-zone", "movement-zone");
    document.body.appendChild(this.movementZone);

    // Create turret joystick zone
    this.turretZone = document.createElement("div");
    this.turretZone.id = "turret-joystick";
    this.turretZone.classList.add("joystick-zone", "turret-zone");
    document.body.appendChild(this.turretZone);

    // Add shoot button
    const shootButton = document.createElement("div");
    shootButton.id = "shoot-button";
    shootButton.classList.add("mobile-button");
    shootButton.innerText = "FIRE";
    document.body.appendChild(shootButton);

    // Add handbrake button
    const handbrakeButton = document.createElement("div");
    handbrakeButton.id = "handbrake-button";
    handbrakeButton.classList.add("mobile-button");
    handbrakeButton.innerText = "BRAKE";
    document.body.appendChild(handbrakeButton);

    // Setup button events
    this.setupButtonEvents(shootButton, handbrakeButton);
  }

  private setupButtonEvents(
    shootButton: HTMLElement,
    handbrakeButton: HTMLElement
  ): void {
    // Shoot button events
    shootButton.addEventListener("touchstart", (e) => {
      e.preventDefault();
      if (this.tank && !this.tank.isDestroyed) {
        // Get the scene from window.gameState
        const scene = window.gameState?.scene;
        if (scene) {
          this.tank.shoot(scene);
        }
      }
    });

    // Handbrake button events
    handbrakeButton.addEventListener("touchstart", (e) => {
      e.preventDefault();
      if (this.tank && !this.tank.isDestroyed) {
        this.tank.startHandbrake();
      }
    });

    handbrakeButton.addEventListener("touchend", (e) => {
      e.preventDefault();
      if (this.tank && !this.tank.isDestroyed) {
        this.tank.stopHandbrake();
      }
    });
  }

  private setupMovementJoystickEvents(): void {
    if (!this.movementJoystick) return;

    // Handle joystick movement
    this.movementJoystick.on("move", (evt, data) => {
      if (!this.tank || this.tank.isDestroyed) return;

      const angle = data.angle.radian;
      const force = Math.min(1, data.force / 2); // Normalize force

      // Forward/backward movement (up = forward, down = backward)
      const forwardAmount = Math.sin(angle) * force;
      // Left/right turning (left = left turn, right = right turn)
      const turnAmount = Math.cos(angle) * force;

      console.log(
        "[MobileController] Movement: angle=" +
          (angle * (180 / Math.PI)).toFixed(1) +
          "°, forward=" +
          forwardAmount.toFixed(2) +
          ", turn=" +
          turnAmount.toFixed(2)
      );

      // Reset all movement controls first
      this.tank.stopMovingForward();
      this.tank.stopMovingBackward();
      this.tank.stopTurningLeft();
      this.tank.stopTurningRight();

      // Apply forward/backward movement
      if (forwardAmount > 0.2) {
        this.tank.startMovingForward();
      } else if (forwardAmount < -0.2) {
        this.tank.startMovingBackward();
      }

      // Apply turning
      if (turnAmount < -0.2) {
        this.tank.startTurningLeft();
      } else if (turnAmount > 0.2) {
        this.tank.startTurningRight();
      }
    });

    // Handle joystick end
    this.movementJoystick.on("end", () => {
      if (!this.tank || this.tank.isDestroyed) return;

      // Stop all movement
      this.tank.stopMovingForward();
      this.tank.stopMovingBackward();
      this.tank.stopTurningLeft();
      this.tank.stopTurningRight();
    });
  }

  private setupTurretJoystickEvents(): void {
    if (!this.turretJoystick) return;

    // Handle joystick movement for turret rotation
    this.turretJoystick.on("move", (evt, data) => {
      if (!this.tank || this.tank.isDestroyed) return;

      const angle = data.angle.radian;
      const force = Math.min(1, data.force / 2);

      // Calculate turret angle based on joystick position
      this.tank.targetTurretAngle = angle;

      // Handle barrel elevation with vertical component
      // Use sin for vertical movement (up = 90° = π/2, down = 270° = 3π/2)
      const verticalAmount = Math.sin(angle) * force;

      console.log(
        "[MobileController] Turret elevation: angle=" +
          (angle * (180 / Math.PI)).toFixed(1) +
          "°, verticalAmount=" +
          verticalAmount.toFixed(2)
      );

      // Use handleKeyDown and handleKeyUp methods for barrel control
      if (verticalAmount > 0.3) {
        // Simulate ArrowUp key press (joystick moved up)
        const event = new KeyboardEvent("keydown", { code: "ArrowUp" });
        this.tank.handleKeyDown(event);
      } else if (verticalAmount < -0.3) {
        // Simulate ArrowDown key press (joystick moved down)
        const event = new KeyboardEvent("keydown", { code: "ArrowDown" });
        this.tank.handleKeyDown(event);
      } else {
        // Reset both controls
        const upEvent = new KeyboardEvent("keyup", { code: "ArrowUp" });
        const downEvent = new KeyboardEvent("keyup", { code: "ArrowDown" });
        this.tank.handleKeyUp(upEvent);
        this.tank.handleKeyUp(downEvent);
      }
    });

    // Handle joystick end for turret
    this.turretJoystick.on("end", () => {
      if (!this.tank || this.tank.isDestroyed) return;

      // Reset barrel controls
      const upEvent = new KeyboardEvent("keyup", { code: "ArrowUp" });
      const downEvent = new KeyboardEvent("keyup", { code: "ArrowDown" });
      this.tank.handleKeyUp(upEvent);
      this.tank.handleKeyUp(downEvent);
    });
  }

  // Improved mobile detection that works in both portrait and landscape
  private detectMobile(): boolean {
    // Check for touch capability
    const hasTouchScreen =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;

    // Check for mobile user agent - most reliable indicator of an actual mobile device
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    
    // Add additional check for screen size and orientation
    const isSmallScreen = window.innerWidth <= 1024 || window.innerHeight <= 768;
    
    // We need both a mobile user agent AND either touch capability or small screen
    // This helps avoid detecting touch-capable laptops/desktops as mobile
    const isMobileDevice = isMobileUserAgent && (hasTouchScreen || isSmallScreen);
    
    console.log(
      "[MobileController] Detection details:",
      {
        hasTouchScreen,
        isMobileUserAgent,
        isSmallScreen,
        finalDecision: isMobileDevice
      }
    );

    return isMobileDevice;
  }
  
  // Public accessor for isMobile property
  public getIsMobile(): boolean {
    return this.isMobile;
  }
  
  // Force a recheck of mobile detection and update UI accordingly
  public forceMobileCheck(): void {
    const wasMobile = this.isMobile;
    this.isMobile = this.detectMobile();
    
    console.log("[MobileController] Force check - Mobile state:", this.isMobile);
    
    // Handle state change
    if (wasMobile !== this.isMobile) {
      if (this.isMobile && !this.isInitialized) {
        this.initialize();
      } else if (!this.isMobile && this.isInitialized) {
        this.destroy();
      }
    }
  }

  private setMobileCameraDistance(): void {
    if (
      typeof window.cameraDistance !== "undefined" &&
      typeof window.setCameraDistance !== "undefined"
    ) {
      // Store the original camera distance for restoration later
      this.originalCameraDistance = window.cameraDistance;

      // Set to a closer distance for mobile using the global setter
      window.setCameraDistance(this.mobileCameraDistance);

      console.log(
        "[MobileController] Camera distance set to mobile view: " +
          this.mobileCameraDistance +
          " (original: " +
          this.originalCameraDistance +
          ")"
      );
    } else {
      console.warn(
        "[MobileController] Could not access global camera distance functions"
      );
    }
  }

  private restoreOriginalCameraDistance(): void {
    if (
      this.originalCameraDistance !== null &&
      typeof window.setCameraDistance !== "undefined"
    ) {
      // Restore the original camera distance using the global setter
      window.setCameraDistance(this.originalCameraDistance);
      console.log(
        "[MobileController] Restored original camera distance: " +
          this.originalCameraDistance
      );
    }
  }

  public resize(): void {
    // We now rely on our improved detectMobile method rather than just window width
    const newIsMobile = this.detectMobile();

    // If mobile state changed
    if (this.isMobile !== newIsMobile) {
      this.isMobile = newIsMobile;
      console.log("[MobileController] Mobile state changed to:", this.isMobile);

      // If switched to mobile, initialize
      if (this.isMobile && !this.isInitialized) {
        this.initialize();
      }
      // If switched to desktop, destroy
      else if (!this.isMobile && this.isInitialized) {
        this.destroy();
      }
    }
  }

  public destroy(): void {
    // Destroy joysticks
    if (this.movementJoystick) {
      this.movementJoystick.destroy();
      this.movementJoystick = null;
    }

    if (this.turretJoystick) {
      this.turretJoystick.destroy();
      this.turretJoystick = null;
    }

    // Remove DOM elements
    if (this.movementZone && this.movementZone.parentNode) {
      this.movementZone.parentNode.removeChild(this.movementZone);
      this.movementZone = null;
    }

    if (this.turretZone && this.turretZone.parentNode) {
      this.turretZone.parentNode.removeChild(this.turretZone);
      this.turretZone = null;
    }

    // Remove shoot and handbrake buttons
    const shootButton = document.getElementById("shoot-button");
    if (shootButton && shootButton.parentNode) {
      shootButton.parentNode.removeChild(shootButton);
    }

    const handbrakeButton = document.getElementById("handbrake-button");
    if (handbrakeButton && handbrakeButton.parentNode) {
      handbrakeButton.parentNode.removeChild(handbrakeButton);
    }

    this.restoreOriginalCameraDistance();

    document.body.classList.remove("isMobile");

    this.isInitialized = false;
  }
}
